<template>
  <Detail class="campaign-report" titleI18nKey="admin.crm.label.campaignReport.title" width="auto">
    <template #title-left>
      <div v-rect-loading="exporting" class="export-btn" @click="onExport">
        <i class="btn-icon iconfont iconCRM-export"></i>
      </div>
    </template>
    <div class="search-container">
      <el-select
        v-model="search.microsite"
        class="search-microsite"
        clearable
        filterable
        popper-class="microsite-select"
        :placeholder="$t('admin.crm.placeholder.campaignReport.searchMicrosite')"
        @change="onMicrositeChange"
      >
        <el-option
          v-for="(item, key) in micrositeList"
          :key="key"
          :label="item.name"
          :value="item.id">
        </el-option>
      </el-select>
      <el-select
        v-model="search.campaign"
        class="search-campaign"
        clearable
        filterable
        popper-class="campaign-select"
        :placeholder="$t('admin.crm.placeholder.campaignReport.searchCampaign')"
        @change="onCampaignChange"
      >
        <el-option
          v-for="(item, key) in activeCampaignList"
          :key="key"
          :label="item.name"
          :value="item.id">
        </el-option>
      </el-select>
      <el-date-picker
        v-model="search.dateRange"
        class="search-date"
        type="daterange"
        :clearable="false"
        :picker-options="pickerOptions"
        :start-placeholder="$t('admin.crm.placeholder.campaignReport.startDate')"
        :end-placeholder="$t('admin.crm.placeholder.campaignReport.endDate')"
        @change="searchReportData"
      >
      </el-date-picker>
      <el-select
        v-model="activeCategory"
        class="category-selector"
        filterable
        clearable
        :placeholder="$t('admin.crm.placeholder.campaignReport.category')"
        @change="handleCategoryChange"
      >
        <el-option :label="$t(`admin.crm.label.campaignReport.all`)" value="all"></el-option>
        <el-option
          v-for="(value, key) in category"
          :key="key"
          :label="$t(`admin.crm.label.campaignReport.${key}`)"
          :value="value">
        </el-option>
      </el-select>
    </div>
    <div v-rect-loading="loading" class="chart-container">
      <div ref="categoryChart" :style="`left: ${showDateChart ? '110%' : 0}`" class="category-chart"></div>
      <div ref="dateChart" :style="`left: ${showDateChart ? 0 : '110%'}`" class="date-chart"></div>
    </div>
  </Detail>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import * as echarts from 'echarts'
import { formatBigNumber } from '@/components/filters'
import { categoryChartOption, dateChartOption } from './chartData'
import Detail from '../detail.vue'
import { createI18nProperty, isEn } from '@/i18n'

const dateFormat = 'YYYY-MM-DD'

export default {
  components: {
    Detail
  },
  metaInfo () {
    return {
      style: [
        { innerHTML: '.el-select-dropdown.el-popper.microsite-select{max-width: 566px;}.el-select-dropdown.el-popper.campaign-select{max-width: 588px;}' }
      ]
    }
  },
  data () {
    const momentDate = moment()
    const category = this.getCategoryData()
    return {
      loadingCount: 0,
      reportData: {},
      category: category,
      campaignList: [],
      micrositeList: [],
      categoryChart: null,
      dateChart: null,
      search: {
        campaign: null,
        microsite: null,
        dateRange: [momentDate.subtract(7, 'days').format(), momentDate.add(6, 'days').format()],
        get startDate () {
          const date = this.dateRange && this.dateRange.length > 1 && this.dateRange[0]
          return moment(date).format(dateFormat)
        },
        get endDate () {
          const date = this.dateRange && this.dateRange.length > 1 && this.dateRange[1]
          return moment(date).format(dateFormat)
        }
      },
      activeCategory: null,
      pickerOptions: {
        // 方便测试环境测试，取消日期限制
        disabledDate: process.env.VUE_APP_ENV === 'production' ? (date) => date.valueOf() > momentDate.valueOf() : undefined
      },
      exporting: false
    }
  },
  computed: {
    loading: {
      get () {
        return this.loadingCount > 0
      },
      set (val) {
        this.loadingCount += val ? 1 : -1
      }
    },
    activeCampaignList () {
      let campaignList
      if (this.search.microsite) {
        campaignList = _.filter(this.campaignList, { microsite: this.search.microsite })
      } else {
        campaignList = this.campaignList
      }
      return [
        createI18nProperty({
          id: -1,
          _isMicrositeReportCampaign: true
        }, 'name', 'admin.crm.label.campaignReport.onlyMicrosite'),
        ...campaignList]
    },
    showDateChart () {
      return this.activeCategory && this.activeCategory !== 'all'
    },
    categoryChartData () {
      const categoryData = {}
      _.forEach(this.reportData, item => {
        _.forEach(item, (value, key) => {
          if (categoryData.hasOwnProperty(key)) {
            categoryData[key] = categoryData[key] + value
          } else {
            categoryData[key] = value
          }
        })
      })
      const chartData = []
      _.forEach(this.category, (value, key) => {
        const data = {
          category: this.$t(`admin.crm.label.campaignReport.${key}`),
          value: categoryData[value] || 0
        }
        if (key === 'realSent') {
          data.wx_delivered = categoryData.wx_delivered
          data.email_delivered = categoryData.email_delivered
        } else if (key === 'sent') {
          data.wx_sent = categoryData.wx_sent
          data.email_sent = categoryData.email_sent
        }
        chartData.push(data)
      })
      chartData.reverse()
      return chartData
    },
    dateChartData () {
      if (!this.activeCategory || this.activeCategory === 'all') {
        return []
      }
      const chartData = [['date', ' ']]
      if (this.activeCategory === 'real_sent') {
        chartData[0] = ['date', this.$t('admin.crm.label.campaignReport.realSent'), this.$t('admin.crm.label.campaignReport.wxDelivered'), this.$t('admin.crm.label.campaignReport.emailDelivered')]
      } else if (this.activeCategory === 'sent') {
        chartData[0] = ['date', this.$t('admin.crm.label.campaignReport.sent'), this.$t('admin.crm.label.campaignReport.wxSent'), this.$t('admin.crm.label.campaignReport.emailSent')]
      }
      const startDate = moment(this.search.startDate)
      const endDate = moment(this.search.endDate)
      const days = endDate.diff(startDate, 'days')
      _.times(days, () => {
        const day = startDate.format(dateFormat)
        const dayData = this.reportData[day]
        if (this.activeCategory === 'real_sent') {
          chartData.push([day, dayData?.real_sent || 0, dayData?.wx_delivered || 0, dayData?.email_delivered || 0])
        } else if (this.activeCategory === 'sent') {
          chartData.push([day, dayData?.sent || 0, dayData?.wx_sent || 0, dayData?.email_sent || 0])
        } else {
          const dayValue = (dayData && dayData[this.activeCategory]) || 0
          chartData.push([day, dayValue])
        }
        startDate.add(1, 'days')
      })
      if (chartData.length === 1) {
        if (this.activeCategory === 'real_sent' || this.activeCategory === 'sent') {
          chartData.push([moment().format(dateFormat), 0, 0, 0])
        } else {
          chartData.push([moment().format(dateFormat), 0])
        }
      }
      return chartData
    }
  },
  created () {
    this.initData()
  },
  mounted () {
    this.initChart()
  },
  methods: {
    ...mapActions('crm', ['searchMicrosite', 'getCampaignReport', 'exportCampaignReport', 'createDownloadFile']),
    ...mapGetters('account', ['getCurrentAccount']),
    async initData () {
      this.loading = true
      try {
        const query = this.$route.query
        if (query.campaign) {
          this.search.campaign = parseInt(query.campaign) || null
        }
        if (query.microsite) {
          this.search.microsite = parseInt(query.microsite) || null
        }
        const resp = await this.searchMicrosite({ data_type: 'tree_campaign' })
        if (resp) {
          const micrositeList = resp.microsite
          const campaignList = resp.campaign
          this.micrositeList = micrositeList
          this.campaignList = campaignList
        }
      } finally {
        this.loading = false
      }
    },
    async searchReportData () {
      this.loading = true
      try {
        if (!this.search.microsite && !this.search.campaign) {
          this.reportData = {}
          this.refreshChartData()
          return
        }
        const params = {
          microsite_id: this.search.microsite,
          campaign_id: this.search.campaign === -1 ? null : this.search.campaign,
          start_date: this.search.startDate,
          end_date: this.search.endDate,
          only_microsite: this.search.campaign === -1 ? 1 : null
        }
        const resp = await this.getCampaignReport(params)
        if (resp) {
          const reportData = {}
          _.forEach(resp.records, (item) => {
            reportData[item.report_date] = item
            item.sent = _.max([item.wx_sent, item.email_sent])
            item.real_sent = _.max([item.wx_delivered, item.email_delivered])
          })
          this.reportData = reportData
          this.refreshChartData()
        }
      } finally {
        this.loading = false
      }
    },
    initChart () {
      const categoryChartEl = this.$refs.categoryChart
      this.categoryChart = echarts.init(categoryChartEl)
      categoryChartOption.tooltip.formatter = (params) => {
        const param = params[0]
        const label = param.name
        const index = param.dataIndex
        const value = param.data.value
        let tooltipData = `${label}${isEn() ? ': ' : '：'}${formatBigNumber(value)}`
        const data = this.categoryChartData[index]
        if (data) {
          if (data.hasOwnProperty('wx_delivered')) {
            const wxDelivered = this.$t('admin.crm.message.campaignReport.wxDelivered', { count: data.wx_delivered || 0 })
            const emailDelivered = this.$t('admin.crm.message.campaignReport.emailDelivered', { count: data.email_delivered || 0 })
            tooltipData += `<br><span style="display: inline-block; padding-left: 12px;">${wxDelivered}<br>${emailDelivered}</span>`
          } else if (data.hasOwnProperty('wx_sent')) {
            const wxSent = this.$t('admin.crm.message.campaignReport.wxSent', { count: data.wx_sent || 0 })
            const emailSent = this.$t('admin.crm.message.campaignReport.emailSent', { count: data.email_sent || 0 })
            tooltipData += `<br><span style="display: inline-block; padding-left: 12px;">${wxSent}<br>${emailSent}</span>`
          }
        }
        if (index + 1 !== this.categoryChartData.length) {
          const hasSoftUser = this.category.potentialCandidateCount
          // index == 4 is talent pool count ,this metric doesn't need rate
          if (index !== 4 && !(hasSoftUser && index === 5)) {
            let nextValue = this.categoryChartData[index + 1]
            if (index === 3) {
              if (hasSoftUser) {
                nextValue = this.categoryChartData[index + 3]
              } else {
                nextValue = this.categoryChartData[index + 2]
              }
            }
            let rate = 0
            if (nextValue && nextValue.value) {
              rate = (value / nextValue.value * 100).toFixed(2)
            }
            tooltipData += `<br>${this.$t('admin.crm.label.campaignReport.conversionRate', { rate })}`
          }
        }

        return tooltipData
      }
      this.categoryChart.setOption(categoryChartOption)

      const dateChartEl = this.$refs.dateChart
      this.dateChart = echarts.init(dateChartEl)
      this.dateChart.setOption(dateChartOption)

      this.searchReportData()
    },
    refreshChartData () {
      if (this.showDateChart) {
        if (this.activeCategory === 'real_sent' || this.activeCategory === 'sent') {
          this.dateChart.setOption({
            dataset: { source: this.dateChartData },
            series: [
              {
                type: 'line',
                symbol: 'circle',
                symbolSize: 5,
                showSymbol: true,
                color: '#badbf4',
                areaStyle: {
                  color: '#badbf4'
                },
                smooth: true
              },
              {
                type: 'line',
                symbol: 'circle',
                symbolSize: 5,
                showSymbol: true,
                color: '#44b549',
                areaStyle: {
                  color: '#44b549'
                },
                smooth: true
              },
              {
                type: 'line',
                symbol: 'circle',
                symbolSize: 5,
                showSymbol: true,
                color: '#0096cc',
                areaStyle: {
                  color: '#0096cc'
                },
                smooth: true
              }
            ]
          })
        } else {
          this.dateChart.setOption({
            dataset: { source: this.dateChartData }
          })
        }
      } else {
        this.categoryChart.setOption({
          dataset: {
            source: this.categoryChartData
          },
          yAxis: [{}, { data: this.categoryChartData.map(item => item.value) }]
        })
      }
    },
    onMicrositeChange () {
      if (this.search.campaign && this.search.campaign !== -1) {
        this.search.campaign = null
      }
      this.searchReportData()
    },
    onCampaignChange () {
      if (this.search.campaign && this.search.campaign !== -1) {
        const campaign = _.find(this.campaignList, { id: this.search.campaign })
        if (campaign && this.search.microsite !== campaign.microsite) {
          this.search.microsite = campaign.microsite
        } else if (!campaign) {
          this.search.microsite = null
        }
      }
      this.searchReportData()
    },
    handleCategoryChange () {
      this.refreshChartData()
    },
    async onExport () {
      if (this.exporting) {
        return
      }
      this.exporting = true
      const params = {
        microsite_id: this.search.microsite,
        campaign_id: this.search.campaign === -1 ? null : this.search.campaign,
        start_date: this.search.startDate,
        end_date: this.search.endDate,
        only_microsite: this.search.campaign === -1 ? 1 : null
      }
      await this.downloadFile(params)
      const finishExport = () => { this.exporting = false }
      setTimeout(finishExport, 1000)
    },
    async downloadFile (exportData) {
      const response = await this.exportCampaignReport(exportData)
      await this.createDownloadFile(response)
    },
    getCategoryData () {
      const currentUser = this.getCurrentAccount()
      let category = null
      if (currentUser.premiums.includes('PREMIUM_SOFT_USER')) {
        category = {
          sent: 'sent',
          realSent: 'real_sent',
          micrositePV: 'microsite_campaign_pv',
          jobPV: 'job_pv',
          potentialCandidateCount: 'potential_candidate_count',
          newTalentCount: 'new_talent_count',
          applied: 'applied',
          screened: 'screened',
          interviewed: 'interviewed',
          hired: 'hired'
        }
      } else {
        category = {
          sent: 'sent',
          realSent: 'real_sent',
          micrositePV: 'microsite_campaign_pv',
          jobPV: 'job_pv',
          newTalentCount: 'new_talent_count',
          applied: 'applied',
          screened: 'screened',
          interviewed: 'interviewed',
          hired: 'hired'
        }
      }
      return category
    }
  },
  watch: {
    categoryChartData () {
      this.refreshChartData()
    }
  }
}
</script>

<style lang="scss" scoped>
.campaign-report {
  .title {
    color: #666666;
    font-size: 16px;
    font-weight: bold;
  }

  .export-btn {
    margin-left: auto;
    line-height: 32px;
    cursor: pointer;

    .btn-icon {
      font-size: 24px;
      color: #0096cc;
      vertical-align: middle;
    }

    ::v-deep {
      .rect-loading {
        .el-loading-spinner {
          margin-top: -18px;
        }

        .spinner {
          width: 20px;
          height: 20px;

          > div {
            width: 2px;
          }
        }
      }
    }
  }

  .search-container {
    display: flex;

    > * {
      flex: 1;
      margin-left: 12px;
    }

    .search-microsite {
      margin-left: 0;
    }

    ::v-deep {
      .el-input__inner {
        height: 32px;
        line-height: 32px;
        border-radius: 2px;
      }

      .el-date-editor .el-range__icon {
        line-height: 26px;
      }

      .el-date-editor .el-range-separator {
        line-height: 24px;
      }
    }
  }

  .chart-container {
    margin: 20px 158px 0;
    min-width: 660px;
    height: 400px;
    overflow: hidden;
    position: relative;

    .category-chart {
      width: 100%;
      height: 100%;
      position: absolute;
    }

    .date-chart {
      width: 100%;
      height: 100%;
      position: absolute;
    }
  }

  .report-container {
    margin-top: 20px;

    .report-header {
      display: flex;

      .category-selector {
        width: 218px;
        margin-right: auto;
      }

      .export-btn {
        margin-right: 0;
        margin-top: auto;
        margin-bottom: auto;
        color: #1597CC;
        cursor: pointer;

        .btn-icon {
          font-size: 24px;
          color: #1597CC;
          vertical-align: middle;
        }

        .btn-label {
          font-size: 14px;
          vertical-align: middle;
          margin-left: 5px;
          margin-right: 20px;
        }
      }
    }
  }
}

.campaign-select,
.microsite-select {
  .el-select-dropdown__item {
    padding: 0 10px;
  }
}
</style>
